import { ensureDrawOverlaysRegistered } from "../utils/drawOverlay";
export function useDrawFlow(ctx) {
  const vm = ctx.vm;
  const closeCtx = () => {
    vm.ctx.open = false;
    vm.ctx.id = null;
    vm.ctx.sub = "";
    vm.ctx.isText = false;
  };
  const onCtxDelete = () => {
    const id = vm.ctx.id;
    if (!id) return;
    vm.removeTracked(id);
    closeCtx();
  };
  const onCtxClone = () => {
    const id = vm.ctx.id;
    if (!id) return;
    const meta = vm.overlayMeta.get(id);
    if (!meta) return;
    if (meta.groupId) {
      const members = Array.from(vm.overlayMeta.entries()).filter(([, m]) => m.groupId === meta.groupId);
      if (!members.length) return;
      const newGid = `cross_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const bump = (p) => ({ timestamp: p.timestamp, value: Number(p.value) + Number(p.value) * 0.0005 });
      let newSelected = null;
      for (const [, m] of members) {
        const newPoints = (m.points || []).map(bump);
        const newExtend = m.extendData ? JSON.parse(JSON.stringify(m.extendData)) : undefined;
        const newId = vm.createOverlayTracked({
          name: m.name,
          paneId: m.paneId,
          points: newPoints,
          extendData: newExtend,
          styles: m.styles ? JSON.parse(JSON.stringify(m.styles)) : undefined,
          zLevel: m.zLevel,
          groupId: newGid,
          lock: true,
          visible: true,
        });
        if (newId && (m.name === "kiteSticker" || m.name === "kiteArrowSticker")) {
          const p0 = newPoints?.[0];
          if (p0) vm.stickerStore.set(newId, { name: m.name, point: p0, extend: newExtend || {} });
        }
        if (newId && m.name === "kiteCrosslineTag") newSelected = newId;
        if (!newSelected && newId) newSelected = newId;
      }
      if (newSelected) vm.selectedOverlayId = newSelected;
      closeCtx();
      return;
    }
    const newPoints = (meta.points || []).map((p) => ({
      timestamp: p.timestamp,
      value: Number(p.value) + Number(p.value) * 0.0005,
    }));
    const newExtend = meta.extendData ? JSON.parse(JSON.stringify(meta.extendData)) : undefined;
    const newId = vm.createOverlayTracked({
      name: meta.name,
      paneId: meta.paneId,
      points: newPoints,
      extendData: newExtend,
      styles: meta.styles ? JSON.parse(JSON.stringify(meta.styles)) : undefined,
      zLevel: meta.zLevel,
    });
    if (!newId) return;
    if (meta.name === "kiteSticker" || meta.name === "kiteArrowSticker") {
      const p0 = newPoints?.[0];
      if (p0) vm.stickerStore.set(newId, { name: meta.name, point: p0, extend: newExtend || {} });
    }
    vm.selectedOverlayId = newId;
    vm.ctx.id = newId;
    closeCtx();
  };
  const hitTestAnyDrawing = (mx, my) => {
    for (const [id, meta] of Array.from(vm.overlayMeta.entries()).reverse()) {
      if (meta.name !== "kiteAnnotation") continue;
      const p0 = meta.points?.[0];
      const px = p0 && vm.pointToPixel(p0);
      if (!px) continue;
      const r = 35;
      if (vm.dist2(mx, my, px.x, px.y) <= r * r) return { id };
    }
    for (const [id, meta] of Array.from(vm.overlayMeta.entries()).reverse()) {
      if (meta.name !== "kiteCrosslineTag") continue;
      const p0 = meta.points?.[0];
      const px = p0 && vm.pointToPixel(p0);
      if (!px) continue;
      const r = 16;
      if (vm.dist2(mx, my, px.x, px.y) <= r * r) return { id };
    }
    const st = vm.hitTestArrow(mx, my) || vm.hitTestStickerBody(mx, my);
    if (st?.id) return { id: st.id };
    const entries = Array.from(vm.overlayMeta.entries());
    for (let i = entries.length - 1; i >= 0; i--) {
      const [id, meta] = entries[i];
      const pts = meta.points || [];
      for (const p of pts) {
        const px = vm.pointToPixel(p);
        if (!px) continue;
        const r = 10;
        if (vm.dist2(mx, my, px.x, px.y) <= r * r) return { id };
      }
    }
    return null;
  };
  const onContextMenu = (e) => {
    if (!ctx.chart()) return;
    e.preventDefault();
    const box = ctx.rootEl().getBoundingClientRect();
    const mx = e.clientX - box.left;
    const my = e.clientY - box.top;
    const hit = hitTestAnyDrawing(mx, my);
    if (!hit?.id) return closeCtx();
    const meta0 = vm.overlayMeta.get(hit.id);
    if (meta0?.groupId) {
      const tagEntry = Array.from(vm.overlayMeta.entries()).find(
        ([, m]) => m.groupId === meta0.groupId && m.name === "kiteCrosslineTag"
      );
      if (tagEntry) hit.id = tagEntry[0];
    }
    vm.selectedOverlayId = hit.id;
    const meta = vm.overlayMeta.get(hit.id);
    const W = ctx.rootEl().clientWidth || 0;
    const H = ctx.rootEl().clientHeight || 0;
    const menuW = 230;
    const menuH = 200;
    vm.ctx.open = true;
    vm.ctx.sub = "";
    vm.ctx.id = hit.id;
    vm.ctx.isText = !!(meta && meta.name === "kiteAnnotation");
    vm.ctx.x = Math.max(0, Math.min(mx, W - menuW));
    vm.ctx.y = Math.max(0, Math.min(my, H - menuH));
    vm.placeCtxMenu(vm.ctx.x, vm.ctx.y);
  };
  const startDrawing = (overlayName, statusCb) => {
    const raw = String(overlayName || "").trim();
    const [name, kind] = raw.split(":");
    if (!ctx.chart()) return;
    ensureDrawOverlaysRegistered();
    if (!name) return;
    vm.resetFreehand();
    vm.pendingTextTool = null;
    vm.annotationMode = false;
    vm.ann.open = false;
    vm.pendingCallout = null;
    vm.drawTool.active = false;
    vm.drawTool.name = "";
    vm.drawTool.points = [];
    vm.drawTool.steps = 0;
    vm.stickerTool = "";
    vm.stickerDrag.active = false;
    if (name === "kiteAnnotation") {
      vm.annotationMode = true;
      try { ctx.chart().setScrollEnabled?.(false); } catch (_) { }
      try { ctx.chart().setZoomEnabled?.(false); } catch (_) { }
      if (typeof statusCb === "function") statusCb("Click on chart to place annotation.");
      return;
    }
    if (name === "kiteSticker") {
      vm.stickerTool = "kiteSticker";
      vm.stickerKind = kind || "check";
      try { ctx.chart().setScrollEnabled?.(false); } catch (_) { }
      try { ctx.chart().setZoomEnabled?.(false); } catch (_) { }
      if (typeof statusCb === "function") statusCb("Click to place sticker. Drag to move. Use handles to resize/rotate.");
      return;
    }
    if (name === "kiteArrowSticker") {
      vm.stickerTool = "kiteArrowSticker";
      vm.stickerKind = "arrow";
      try { ctx.chart().setScrollEnabled?.(false); } catch (_) { }
      try { ctx.chart().setZoomEnabled?.(false); } catch (_) { }
      if (typeof statusCb === "function") statusCb("Click to place arrow. Drag handles to resize/rotate.");
      return;
    }
    if (name === "kiteCrossLine") {
      vm.pendingTextTool = "kiteCrossLine";
      try { ctx.chart().setScrollEnabled?.(false); } catch (_) { }
      try { ctx.chart().setZoomEnabled?.(false); } catch (_) { }
      if (typeof statusCb === "function") statusCb("Click to place crossline.");
      return;
    }
    if (name === "kiteDoodle") {
      vm.freehand.tool = "kiteDoodle";
      vm.freehand.drawing = false;
      vm.freehand.id = null;
      vm.freehand.points = [];
      vm.freehand.lastPx = null;
      try { ctx.chart().setScrollEnabled?.(false); } catch (_) { }
      try { ctx.chart().setZoomEnabled?.(false); } catch (_) { }
      if (typeof statusCb === "function") statusCb("Hold left mouse button and draw.");
      return;
    }
    const stepMap = {
      kiteTrendLine: 2,
      kiteAverageLine: 2,
      kiteChannel: 3,
      kiteCross: 1,
      kiteEllipse: 2,
      kiteElliottWave: 5,
    };
    vm.drawTool.active = true;
    vm.drawTool.name = name;
    vm.drawTool.steps = stepMap[name] || 2;
    vm.drawTool.points = [];
    vm.drawTool.statusCb = typeof statusCb === "function" ? statusCb : null;
    try { ctx.chart().setScrollEnabled?.(false); } catch (_) { }
    try { ctx.chart().setZoomEnabled?.(false); } catch (_) { }
    vm.drawTool.statusCb?.(`Drawing ${name}: click ${vm.drawTool.steps} point(s).`);
  };
  const stopDrawing = () => {
    vm.annotationMode = false;
    vm.ann.open = false;
    vm.ann.text = "";
    vm.ann.point = null;
    vm.ann.editingId = null;
    vm.drawTool.active = false;
    vm.drawTool.name = "";
    vm.drawTool.steps = 0;
    vm.drawTool.points = [];
    vm.drawTool.statusCb = null;
    vm.stickerTool = "";
    vm.stickerDrag.active = false;
    vm.stickerDrag.id = null;
    vm.stickerDrag.mode = "";
    vm.stickerDrag.centerPx = null;
    vm.resetFreehand();
    vm.pendingTextTool = null;
    try { ctx.chart().setScrollEnabled?.(true); } catch (_) { }
    try { ctx.chart().setZoomEnabled?.(true); } catch (_) { }
  };
  const clearDrawings = () => {
    if (!ctx.chart()) return;
    const ids = Array.isArray(vm.drawing.createdIds) ? [...vm.drawing.createdIds] : [];
    for (const id of ids) {
      try { ctx.chart().removeOverlay?.({ id }); } catch (_) { }
    }
    vm.drawing.createdIds = [];
    vm.stickerStore.clear();
    vm.overlayMeta.clear();
  };
  const undoDrawing = () => {
    if (!ctx.chart()) return;
    const ids = Array.isArray(vm.drawing.createdIds) ? vm.drawing.createdIds : [];
    const last = ids.pop();
    if (!last) return;
    try { ctx.chart().removeOverlay?.({ id: last }); } catch (_) { }
    vm.overlayMeta.delete(last);
    vm.stickerStore.delete(last);
  };
  const onChartClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (vm.ctx?.open) return;
    if (!ctx.chart()) return;
    const box = ctx.rootEl().getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const picked = vm.pickPointFromMouse(x, y);
    if (!picked) return;
    if (vm.pendingTextTool === "kiteCrossLine") {
      const paneId = ctx.getMainPaneId();
      const gid = `cross_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      vm.createOverlayTracked({
        name: "horizontalStraightLine",
        paneId,
        points: [{ timestamp: picked.timestamp, value: picked.value }],
        groupId: gid,
        zLevel: 10,
      });
      vm.createOverlayTracked({
        name: "verticalStraightLine",
        paneId,
        points: [{ timestamp: picked.timestamp, value: picked.value }],
        groupId: gid,
        zLevel: 10,
      });
      const bars = vm.getBarsSafe();
      const bar = bars?.[picked.dataIndex];
      const ts = bar?.timestamp ?? picked.timestamp;
      vm.createOverlayTracked({
        name: "kiteCrosslineTag",
        paneId,
        points: [{ timestamp: picked.timestamp, value: picked.value }],
        extendData: {
          priceText: Number(picked.value).toFixed(2),
          timeText: new Date(ts).toISOString().slice(0, 10),
        },
        groupId: gid,
        zLevel: 10000,
      });
      vm.pendingTextTool = null;
      try { ctx.chart().setScrollEnabled?.(true); } catch (_) { }
      try { ctx.chart().setZoomEnabled?.(true); } catch (_) { }
      return;
    }
    if (vm.annotationMode) {
      if (vm.ann.open) return;
      const root = ctx.rootEl();
      const maxLeft = Math.max(0, (root.clientWidth || 0) - 240);
      const maxTop = Math.max(0, (root.clientHeight || 0) - 40);
      const left = Math.max(0, Math.min(x + 6, maxLeft));
      const top = Math.max(0, Math.min(y - 12, maxTop));
      vm.ann.open = true;
      vm.ann.left = left;
      vm.ann.top = top;
      vm.ann.point = { timestamp: picked.timestamp, value: picked.value };
      vm.ann.text = "";
      vm.placeAnnBox(left, top);
      ctx.nextTick(() => setTimeout(() => ctx.refs().annInput?.focus?.(), 0));
      return;
    }
    if (vm.drawTool?.active && vm.drawTool?.name) {
      vm.drawTool.points.push({ timestamp: picked.timestamp, value: picked.value, dataIndex: picked.dataIndex });
      const need = Number(vm.drawTool.steps || 2);
      vm.drawTool.statusCb?.(`Point ${vm.drawTool.points.length}/${need}`);
      if (vm.drawTool.points.length < need) return;
      const name = vm.drawTool.name;
      const cleanPoints = vm.drawTool.points.map((p) => ({ timestamp: p.timestamp, value: p.value }));
      const needsGroup = ["kiteRect", "kiteEllipse"].includes(name);
      const gid = needsGroup ? vm.newGroupId(name) : null;
      try {
        vm.createOverlayTracked({
          name,
          paneId: ctx.getMainPaneId(),
          points: cleanPoints,
          groupId: gid,
        });
      } catch (err) {
        console.error("createOverlay failed:", err);
      }
      vm.drawTool.active = false;
      vm.drawTool.name = "";
      vm.drawTool.steps = 0;
      vm.drawTool.points = [];
      vm.drawTool.statusCb = null;
      try { ctx.chart().setScrollEnabled?.(true); } catch (_) { }
      try { ctx.chart().setZoomEnabled?.(true); } catch (_) { }
    }
  };
  const onRootPointerDown = (e) => {
    if (!ctx.chart()) return;
    if (e.button === 2) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.button !== 0) return;
    if (vm.ctx.open) closeCtx();
    vm.onCrossDragDown(e);
    if (vm.crossDrag.active) return;
    vm.onStickerMouseDown(e);
    if (vm.stickerDrag.active) return;
    vm.onFreehandDown(e);
  };
  const onWinPointerMove = (e) => {
    if (!ctx.chart()) return;
    if (vm.crossDrag.active) return vm.onCrossDragMove(e);
    if (vm.stickerDrag.active) return vm.onStickerMouseMove(e);
    if (vm.freehand.drawing) return vm.onFreehandMove(e);
    return vm.onStickerHoverMove(e);
  };
  const onWinPointerUp = () => {
    if (vm.crossDrag.active) vm.onCrossDragUp();
    if (vm.stickerDrag.active) vm.onStickerMouseUp();
    if (vm.freehand.drawing) vm.onFreehandUp();
  };
  return {
    closeCtx,
    onCtxDelete,
    onCtxClone,
    hitTestAnyDrawing,
    onContextMenu,
    startDrawing,
    stopDrawing,
    clearDrawings,
    undoDrawing,
    onChartClick,
    onRootPointerDown,
    onWinPointerMove,
    onWinPointerUp,
  };
}
